const SOP_EXCERPT = `# Synthetic Redacted SOP Excerpt
## Portfolio Demonstration Only — Not an Employer-Controlled Document

**Document ID:** SOP-PQ-###  
**Title:** Controlled Preparation and Loading of [REDACTED] Process Material  
**Revision:** X  
**Effective Date:** [SYNTHETIC]  
**Owner:** Product Quality / Manufacturing

> **Confidentiality notice:** This excerpt is entirely synthetic. Product names, limits, equipment, process conditions, identifiers and acceptance criteria are fictional or intentionally redacted.

## 1. PURPOSE
To define the controlled preparation, handling and loading requirements for **[REDACTED PROCESS MATERIAL]** used during the manufacture and Product Quality evaluation of **[SYNTHETIC PRODUCT FAMILY]**, with the objective of reducing avoidable process variation and ensuring repeatable execution.

## 2. RESPONSIBILITIES
### 2.1 Manufacturing / Product Quality Operator
The operator shall:
1. Verify that the current approved revision of this procedure is available before beginning the activity.
2. Confirm the identity and status of required materials and equipment.
3. Record required lot and equipment identifiers contemporaneously.
4. Perform the preparation and loading sequence as described below.
5. Stop the activity and escalate any condition that meets the defined abnormal-condition criteria.

### 2.2 Product Quality / Technical Reviewer
The reviewer shall:
1. Assess deviations from the defined process.
2. Confirm completion and traceability of required records.
3. Determine whether additional technical assessment, deviation initiation or escalation is required.

## 3. PRE-USE VERIFICATION
Before initiating the procedure, verify:
- required material is within its approved use period;
- material identity matches the applicable manufacturing record;
- equipment status is acceptable for use;
- required components are visibly free of damage or contamination;
- process prerequisites are satisfied.

**Do not proceed if any required pre-use condition cannot be verified.**

## 4. PROCEDURE
### 4.1 Preparation
1. Allow **[REDACTED MATERIAL]** to reach the defined process condition specified in **[CONTROLLED RECORD / SPECIFICATION]**.
2. Mix using the approved technique. Avoid introducing visible foam or entrained air.
3. Inspect the prepared material under normal work-area illumination.
4. If visible bubbles, particulate matter or an abnormal appearance are observed, do not continue to loading; escalate per the abnormal-condition section.

### 4.2 Loading
1. Use the approved calibrated transfer device.
2. Position the transfer device to minimize air introduction.
3. Dispense the required volume using the defined controlled rate.
4. Do not repeatedly aspirate/dispense unless specifically required by the approved method.
5. After loading, visually verify the defined acceptance condition before proceeding.

> **Critical process note:** Avoid uncontrolled agitation or rapid transfer conditions that may promote air entrainment or inconsistent loading.

## 5. DOCUMENTATION
Record at the time the activity is performed:
- date/time;
- operator identifier;
- material lot;
- component lot where applicable;
- equipment identifier;
- required process confirmation;
- observed abnormal condition, if any.

Corrections to controlled records shall follow applicable GDP requirements. Original information shall remain legible and traceable.

## 6. CHANGE RATIONALE — SYNTHETIC EXAMPLE
**Previous state:** The prior instruction described material transfer generally and did not provide explicit controls for air introduction or escalation criteria.

**Technical finding:** Investigation evidence identified transfer handling as a plausible contributor to intermittent signal artifacts.

**Controlled change:** The revised procedure introduced explicit pre-use verification, controlled transfer expectations, visual checks, abnormal-condition criteria and documentation requirements.

**Effectiveness approach:** Product Quality failure recurrence and process-adherence evidence are trended after implementation.

### Writing-style points to annotate on the site
- action-oriented instructions
- clear SHALL / DO NOT expectations
- objective verification
- defined escalation
- traceability
- GDP / ALCOA+
- investigation → change → document → training → effectiveness
`;

export default SOP_EXCERPT;
